import { MAX_ATTEMPTS } from '../constants';
import { Stats } from './stats';

/**
 * Today's score, from how many attempts the flag took.
 *
 * Finding it in one or two goes is a clean sweep and scores full marks; after
 * that the score decays exponentially, because each extra tile makes the flag
 * substantially easier to recognise. Missing it entirely scores nothing.
 *
 * Every number here is a knob — change these three and the whole curve moves.
 */
export const MAX_SCORE = 100;
/** Attempts up to and including this still score full marks. */
export const FREE_ATTEMPTS = 2;
/** Multiplier applied per attempt beyond FREE_ATTEMPTS. */
export const SCORE_DECAY = 0.7;

/**
 * Resulting curve at the current settings:
 *   1-2 -> 100, 3 -> 70, 4 -> 49, 5 -> 34, 6 -> 24, DNF -> 0
 */
export const scoreForAttempts = (attempts: number | 'DNF'): number => {
  if (attempts === 'DNF' || attempts < 1 || attempts > MAX_ATTEMPTS) return 0;
  const penalised = Math.max(0, attempts - FREE_ATTEMPTS);
  return Math.round(MAX_SCORE * SCORE_DECAY ** penalised);
};

/**
 * Mean attempts across the games this player has won, from the local stats the
 * app already keeps. Returns null until there is at least one win, so the end
 * screen can leave the slot empty rather than print a misleading 0.
 */
export const averageGuesses = (stats: Stats): number | null => {
  const distribution = stats.guessDistribution ?? [];
  const games = distribution.reduce((total, count) => total + count, 0);
  if (games === 0) return null;

  const attempts = distribution.reduce(
    (total, count, index) => total + count * (index + 1),
    0,
  );
  return Math.round((attempts / games) * 10) / 10;
};
